import React, { useState, useContext, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { AuthContext, supabase } from '../../context/AuthContext';
import logger from '../../utils/logger';
import './DashboardLayout.css';

const DashboardLayout = () => {
  // eslint-disable-next-line no-unused-vars
  const { user, logout, subscriptionStatus, startFreeTrial } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [conversations, setConversations] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [loading, setLoading] = useState(true);

  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';

  // Fetch agents for the current user
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const { data, error } = await supabase
          .from('agents')
          .select('*')
          .eq('created_by', user.id)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        setAgents(data || []);
        
        // If we already have a currentAgent, keep it
        if (currentAgent) {
          return;
        }
        
        // Check for project_id in URL params or try to get it from conversation
        const projectId = params.projectId;
        if (projectId && data.length > 0) {
          const found = data.find(agent => agent.project_id === projectId);
          setCurrentAgent(found || null);
        } else if (params.sessionId) {
          // If we're in a conversation, fetch the conversation to get its project_id
          const { data: convData } = await supabase
            .from('conversations')
            .select('project_id')
            .eq('session_id', params.sessionId)
            .single();
            
          if (convData && data.length > 0) {
            const found = data.find(agent => agent.project_id === convData.project_id);
            setCurrentAgent(found || null);
          }
        }
      } catch (error) {
        logger.error('Error fetching agents:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAgents();
  }, [user.id, params.projectId, params.sessionId]);

  // Fetch conversation history for the current agent
  useEffect(() => {
    if (!currentAgent) return;
    
    const fetchConversations = async () => {
      try {
        const { data, error } = await supabase
          .from('conversations')
          .select('*')
          .eq('project_id', currentAgent.project_id)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        setConversations(data || []);
      } catch (error) {
        logger.error('Error fetching conversations:', error);
      }
    };
    
    fetchConversations();
  }, [currentAgent]);

  const selectAgent = (agent) => {
    setCurrentAgent(agent);
    setDropdownOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleCreateAgent = () => {
    navigate('/dashboard/create-agent');
    setDropdownOpen(false);
  };

  const handleEditAgent = () => {
    if (currentAgent) {
      navigate(`/dashboard/edit-agent/${currentAgent.project_id}`);
      setDropdownOpen(false);
    }
  };

  const handleAgentStats = () => {
    if (currentAgent) {
      navigate(`/dashboard/agent-stats/${currentAgent.project_id}`);
      setDropdownOpen(false);
    }
  };

  const handleChatView = () => {
    if (currentAgent) {
      // Just navigate to dashboard, keeping the current agent in state
      navigate('/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handlePlanAction = async () => {
    // Always navigate to pricing page
    navigate('/pricing');
  };

  return (
    <div className="dashboard-layout">
      <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <button 
          className="sidebar-collapse-button"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? '→' : '←'}
        </button>
        
        <div className="sidebar-content">
          <div className="sidebar-top">
            <div className="agent-selector" onClick={toggleDropdown}>
              <div className="agent-selector-content">
                <div className="agent-avatar">
                  {currentAgent && currentAgent.chatbot_avatar ? (
                    <img src={currentAgent.chatbot_avatar} alt={currentAgent.project_name || 'Agent'} />
                  ) : (
                    <span>{currentAgent ? currentAgent.project_name?.charAt(0).toUpperCase() : 'A'}</span>
                  )}
                </div>
                {!sidebarCollapsed && currentAgent && <span className="agent-name">{currentAgent.project_name || 'Agent'}</span>}
              </div>
              {!sidebarCollapsed && <span className="dropdown-icon">▼</span>}
            </div>

            {!sidebarCollapsed && dropdownOpen && (
              <div className="agent-dropdown open">
                <ul className="agent-dropdown-list">
                  {agents.map((agent) => (
                    <li 
                      key={agent.project_id} 
                      className={`agent-dropdown-item ${currentAgent && agent.project_id === currentAgent.project_id ? 'active' : ''}`}
                      onClick={() => selectAgent(agent)}
                    >
                      <div className="agent-avatar">
                        {agent.chatbot_avatar ? (
                          <img src={agent.chatbot_avatar} alt={agent.project_name || 'Agent'} />
                        ) : (
                          <span>{agent.project_name ? agent.project_name.charAt(0).toUpperCase() : 'A'}</span>
                        )}
                      </div>
                      <span className="agent-name">{agent.project_name || 'Untitled Agent'}</span>
                    </li>
                  ))}
                </ul>
                <div className="agent-dropdown-actions">
                  <div className="agent-action" onClick={handleCreateAgent}>
                    <span className="agent-action-icon">+</span>
                    <span>Create new agent</span>
                  </div>
                  <div className="agent-action" onClick={handleEditAgent}>
                    <span className="agent-action-icon">✎</span>
                    <span>Edit current agent</span>
                  </div>
                  <div className="agent-action" onClick={handleAgentStats}>
                    <span className="agent-action-icon">📊</span>
                    <span>Agent stats</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {!sidebarCollapsed && (
            <div className="conversations-container">
              <div className="conversations-header">
                <h3>Conversations</h3>
                <button className="new-chat-button" onClick={handleChatView} aria-label="Start new chat">
                  <span className="icon-plus">+</span>
                </button>
              </div>
              <ul className="conversation-list">
                {conversations && conversations.length > 0 ? (
                  conversations.map((conv) => (
                    <li 
                      key={conv.id} 
                      onClick={() => navigate(`/dashboard/conversation/${conv.session_id}`)}
                      className={location.pathname.includes(conv.session_id) ? 'active' : ''}
                    >
                      {conv.name || 'Untitled Conversation'}
                    </li>
                  ))
                ) : (
                  <li className="no-conversations">No conversations yet</li>
                )}
              </ul>
            </div>
          )}

          <div className="sidebar-footer">
            {!sidebarCollapsed && (
              <>
                <div className="sidebar-plan-card">
                  <div className="plan-card-header">
                    <h4>Current Plan</h4>
                    <span className={`plan-status ${subscriptionStatus?.active ? 'active' : 'inactive'}`}>
                      {subscriptionStatus?.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="plan-card-name">{subscriptionStatus?.plan || 'Free'}</div>
                  <button
                    className="upgrade-plan-button"
                    onClick={handlePlanAction}
                    disabled={isStartingTrial}
                  >
                    {isStartingTrial ? 'Processing...' : 'View Plans'}
                  </button>
                </div>
                <div className="sidebar-user-section">
                  <div className="sidebar-user-name">{userName}</div>
                  <button onClick={logout} className="sidebar-signout">
                    Sign Out
                  </button>
                </div>
              </>
            )}
            {sidebarCollapsed && (
              <div className="sidebar-collapsed-footer">
                <button 
                  onClick={() => navigate('/pricing')} 
                  className="sidebar-upgrade-icon" 
                  title="Upgrade Plan"
                >
                  ⭐
                </button>
                <button onClick={logout} className="sidebar-signout-icon" title="Sign Out">
                  🚪
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <Outlet context={{ 
          agents, 
          setAgents,
          currentAgent, 
          setCurrentAgent,
          conversations, 
          setConversations,
          loading
        }} />
      </main>
    </div>
  );
};

export default DashboardLayout;
